const WEATHERAPI_BASE = 'https://api.weatherapi.com/v1';
const WEATHERAPI_KEY = '5d87a8c0c7e34a16a8841647251610';

interface WeatherAPIResponse {
  location: {
    name: string;
    region: string;
    country: string;
    lat: number;
    lon: number;
    tz_id: string;
    localtime: string;
  };
  current: {
    temp_c: number;
    temp_f: number;
    condition: {
      text: string;
      icon: string;
      code: number;
    };
    wind_mph: number;
    wind_kph: number;
    wind_degree: number;
    wind_dir: string;
    pressure_mb: number;
    pressure_in: number;
    precip_mm: number;
    precip_in: number;
    humidity: number;
    cloud: number;
    feelslike_c: number;
    feelslike_f: number;
    vis_km: number;
    vis_miles: number;
    uv: number;
    gust_mph: number;
    gust_kph: number;
  };
  forecast?: {
    forecastday: Array<{
      date: string;
      day: {
        maxtemp_c: number;
        maxtemp_f: number;
        mintemp_c: number;
        mintemp_f: number;
        avgtemp_c: number;
        avgtemp_f: number;
        maxwind_mph: number;
        maxwind_kph: number;
        totalprecip_mm: number;
        avghumidity: number;
        condition: {
          text: string;
          icon: string;
        };
        uv: number;
      };
      astro: {
        sunrise: string;
        sunset: string;
        moonrise: string;
        moonset: string;
        moon_phase: string;
        moon_illumination: string;
      };
    }>;
  };
}

export interface WeatherResult {
  temperature: number;
  condition: string;
  location: string;
  high: number;
  low: number;
  icon: string;
  // Extended data from WeatherAPI
  feelsLike?: number;
  humidity?: number;
  windSpeed?: number;
  windDirection?: string;
  pressure?: number;
  visibility?: number;
  uv?: number;
  cloudCover?: number;
  precipitation?: number;
  gustSpeed?: number;
  // Location details
  region?: string;
  country?: string;
  timezone?: string;
  localTime?: string;
  coordinates?: { lat: number; lon: number };
  // Forecast data
  forecast?: Array<{
    date: string;
    maxTemp: number;
    minTemp: number;
    condition: string;
    icon: string;
    precipitation: number;
    humidity: number;
  }>;
  // Astronomy
  astronomy?: {
    sunrise: string;
    sunset: string;
    moonrise: string;
    moonset: string;
    moonPhase: string;
    moonIllumination: string;
  };
}

const emojiForCondition = (conditionText: string, code: number): string => {
  const text = conditionText.toLowerCase();

  // Based on WeatherAPI condition codes
  if (code === 1000) return '☀️'; // Sunny/Clear
  if (code === 1003) return '🌤️'; // Partly cloudy
  if (code === 1006) return '☁️'; // Cloudy
  if (code === 1009) return '☁️'; // Overcast
  if (code === 1030 || code === 1135 || code === 1147) return '🌫️'; // Mist/Fog
  if ([1063, 1180, 1183, 1186, 1189, 1192, 1195, 1240, 1243, 1246].includes(code)) return '🌧️'; // Rain
  if ([1150, 1153, 1168, 1171].includes(code)) return '🌦️'; // Drizzle
  if ([1087, 1273, 1276, 1279, 1282].includes(code)) return '⛈️'; // Thunder
  if ([1066, 1114, 1210, 1213, 1216, 1219, 1222, 1225, 1255, 1258].includes(code)) return '❄️'; // Snow
  if ([1069, 1072, 1198, 1201, 1204, 1207, 1237, 1249, 1252, 1261, 1264].includes(code)) return '🌨️'; // Sleet/Ice

  // Fallback based on text
  if (text.includes('sun') || text.includes('clear')) return '☀️';
  if (text.includes('cloud')) return '☁️';
  if (text.includes('rain')) return '🌧️';
  if (text.includes('snow')) return '❄️';
  if (text.includes('thunder') || text.includes('storm')) return '⛈️';
  if (text.includes('fog') || text.includes('mist')) return '🌫️';

  return '🌡️';
};

const getUserLocation = async (): Promise<{ lat: number; lon: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Error getting user location:', error.message);
        resolve(null);
      },
      {
        timeout: 5000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  });
};

export const fetchWeatherByCity = async (city?: string): Promise<WeatherResult | null> => {
  try {
    let query = city;

    // If no city provided, try to get user's location
    if (!query) {
      const coords = await getUserLocation();
      if (coords) {
        query = `${coords.lat},${coords.lon}`;
      } else {
        // Fallback to IP-based location
        query = 'auto:ip';
      }
    }

    // Fetch current weather with 3-day forecast
    const response = await fetch(
      `${WEATHERAPI_BASE}/forecast.json?key=${WEATHERAPI_KEY}&q=${encodeURIComponent(query)}&days=3&aqi=yes&alerts=yes`
    );

    if (!response.ok) {
      console.error('WeatherAPI request failed', await response.text());
      return null;
    }

    const data: WeatherAPIResponse = await response.json();

    if (!data?.current || !data?.location) return null;

    const { current, location, forecast } = data;
    const condition = current.condition;

    // Build forecast array
    const forecastData = forecast?.forecastday?.map((day) => ({
      date: day.date,
      maxTemp: Math.round(day.day.maxtemp_c),
      minTemp: Math.round(day.day.mintemp_c),
      condition: day.day.condition.text,
      icon: emojiForCondition(day.day.condition.text, 0),
      precipitation: day.day.totalprecip_mm,
      humidity: day.day.avghumidity,
    })) || [];

    // Get today's forecast for high/low temps
    const todayForecast = forecast?.forecastday?.[0];

    return {
      temperature: Math.round(current.temp_c),
      condition: condition.text,
      location: location.name,
      high: todayForecast ? Math.round(todayForecast.day.maxtemp_c) : Math.round(current.temp_c),
      low: todayForecast ? Math.round(todayForecast.day.mintemp_c) : Math.round(current.temp_c),
      icon: emojiForCondition(condition.text, condition.code),
      // Extended data
      feelsLike: Math.round(current.feelslike_c),
      humidity: current.humidity,
      windSpeed: Math.round(current.wind_kph),
      windDirection: current.wind_dir,
      pressure: Math.round(current.pressure_mb),
      visibility: Math.round(current.vis_km),
      uv: current.uv,
      cloudCover: current.cloud,
      precipitation: current.precip_mm,
      gustSpeed: Math.round(current.gust_kph),
      // Location details
      region: location.region,
      country: location.country,
      timezone: location.tz_id,
      localTime: location.localtime,
      coordinates: {
        lat: location.lat,
        lon: location.lon,
      },
      // Forecast
      forecast: forecastData,
      // Astronomy
      astronomy: todayForecast ? {
        sunrise: todayForecast.astro.sunrise,
        sunset: todayForecast.astro.sunset,
        moonrise: todayForecast.astro.moonrise,
        moonset: todayForecast.astro.moonset,
        moonPhase: todayForecast.astro.moon_phase,
        moonIllumination: todayForecast.astro.moon_illumination,
      } : undefined,
    };
  } catch (error) {
    console.error('Error fetching weather', error);
    return null;
  }
};
